/* eslint-disable-next-line no-unused-vars */
import React, { useState, FormEvent, useEffect, useRef, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Building, User, Search, Phone, Eye, EyeOff, X, ChevronLeft, ChevronRight, Loader, Lock, Heart, ArrowUpCircle, ArrowDownCircle, MapPin } from 'lucide-react';
import { gradientText, gradientButton } from '../theme';
import { useSingleSearch } from '../hooks/useSearch';
import { SingleSearchResult, incrementContactClicks, toggleCrush, isProfileLiked, countUserCrushes } from '../services/searchService';
import { supabase } from '../lib/supabase';
import { useAppSettings } from '../hooks/useAppSettings';
import { useUser } from '../hooks/useUser';
import PaymentFlow from '../components/PaymentFlow';
import { useCachedData } from '../hooks/useCachedData';
import { toast } from 'react-hot-toast';

// Interface pour les photos du bucket "une"
interface CarouselPhoto {
  name: string;
  url: string;
}

// Fonction pour récupérer les photos du carrousel
const fetchCarouselPhotos = async (): Promise<CarouselPhoto[]> => {
  try {
    const { data, error } = await supabase.storage
      .from('une')
      .list('', {
        sortBy: { column: 'name', order: 'asc' }
      });
    
    if (error) {
      console.error('Erreur lors de la récupération des photos:', error);
      throw error;
    }
    
    if (!data || data.length === 0) {
      return [];
    }
    
    // Filtrer uniquement les fichiers (pas les dossiers) et vérifier qu'ils sont valides
    const photoFiles = data.filter(item => 
      !item.id.endsWith('/') && 
      item.name && 
      item.name.match(/\.(jpg|jpeg|png|gif|webp)$/i)
    );
    
    // Vérification supplémentaire - si aucun fichier valide n'est trouvé
    if (photoFiles.length === 0) {
      return [];
    }
    
    const photoUrls = await Promise.all(
      photoFiles.map(async (file) => {
        try {
          const { data: { publicUrl } } = supabase.storage
            .from('une')
            .getPublicUrl(file.name);
          
          return {
            name: file.name,
            url: publicUrl
          };
        } catch (err) {
          console.error(`Erreur lors de la récupération de l'URL de ${file.name}:`, err);
          return null;
        }
      })
    );
    
    // Filtrer les URLs nulles (en cas d'erreur)
    const validPhotoUrls = photoUrls.filter(photo => photo !== null) as CarouselPhoto[];
    return validPhotoUrls;
  } catch (err) {
    console.error('Exception lors de la récupération des photos:', err);
    throw err;
  }
};

function SingleSearch() {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { search, isSearching: externalIsSearching, results: externalResults } = useSingleSearch();
  const { getShortTermSearchUnlockPrice } = useAppSettings();
  const { user } = useUser();
  
  const [country, setCountry] = useState(() => localStorage.getItem('single_search_country') || '');
  const [city, setCity] = useState(() => localStorage.getItem('single_search_city') || '');
  const [ageRange, setAgeRange] = useState(() => localStorage.getItem('single_search_ageRange') || '18-25');
  const [gender, setGender] = useState(() => localStorage.getItem('single_search_gender') || 'homme');
  const [sexualOrientation, setSexualOrientation] = useState(() => localStorage.getItem('single_search_sexualOrientation') || 'hetero');
  const [hasSearched, setHasSearched] = useState(false);
  const [showLongTermResults, setShowLongTermResults] = useState(true);
  const [showShortTermResults, setShowShortTermResults] = useState(false);
  const [showAgeRangeOptions, setShowAgeRangeOptions] = useState(false);
  const [showOrientationOptions, setShowOrientationOptions] = useState(false);
  const [hasUnlockedShortTerm, setHasUnlockedShortTerm] = useState(false);
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const ageRangeRef = useRef<HTMLDivElement>(null);
  const cityInputRef = useRef<HTMLInputElement>(null);
  const orientationRef = useRef<HTMLDivElement>(null);
  
  // Utilisation de useCachedData pour les photos du carrousel
  const { 
    data: carouselPhotosData, 
    isLoading: isLoadingPhotos, 
    error: _photoErrorObj,
    invalidateCache: _invalidateCarouselCache
  } = useCachedData<CarouselPhoto[]>(
    'carousel-photos-une',
    fetchCarouselPhotos,
    24 * 60 * 60 * 1000, // Cache de 24 heures
    []
  );
  
  // S'assurer que carouselPhotos n'est jamais null
  const carouselPhotos = carouselPhotosData || [];
  
  // États pour la galerie de photos
  const [showPhotoGallery, setShowPhotoGallery] = useState(false);
  const [selectedProfile, setSelectedProfile] = useState<SingleSearchResult | null>(null);
  const [currentPhotoIndex, setCurrentPhotoIndex] = useState(0);
  const [_photoLoading, setPhotoLoading] = useState(true);
  const [imageLoadError, setImageLoadError] = useState(false);

  // Variables pour gérer le balayage (swipe)
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [touchEnd, setTouchEnd] = useState<number | null>(null);
  
  // Seuil minimum de distance pour un balayage
  const minSwipeDistance = 50;

  // Fonctions pour gérer le balayage tactile
  const onTouchStart = (e: React.TouchEvent) => {
    setTouchEnd(null);
    setTouchStart(e.targetTouches[0].clientX);
  }
  
  const onTouchMove = (e: React.TouchEvent) => {
    setTouchEnd(e.targetTouches[0].clientX);
  }
  
  const onTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > minSwipeDistance;
    const isRightSwipe = distance < -minSwipeDistance;
    
    if (isLeftSwipe) {
      handleNextPhoto(new MouseEvent('click') as any);
    }
    if (isRightSwipe) {
      handlePrevPhoto(new MouseEvent('click') as any);
    }
  }

  // Vérifier si tous les paramètres de recherche nécessaires sont remplis
  const isSearchFormValid = () => {
    const cityValue = cityInputRef.current?.value || '';
    return country.trim() !== '' && cityValue.trim() !== '' && gender !== '';
  };

  // Fermer le menu des tranches d'âge quand on clique ailleurs
  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (ageRangeRef.current && !ageRangeRef.current.contains(event.target as Node)) {
        setShowAgeRangeOptions(false);
      }
      if (orientationRef.current && !orientationRef.current.contains(event.target as Node)) {
        setShowOrientationOptions(false);
      }
    }
    
    function handleScroll() {
      if (showAgeRangeOptions) {
        setShowAgeRangeOptions(false);
      }
      if (showOrientationOptions) {
        setShowOrientationOptions(false);
      }
    }
    
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('scroll', handleScroll);
    
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('scroll', handleScroll);
    };
  }, [showAgeRangeOptions, showOrientationOptions]);

  // Sauvegarder les paramètres dans le localStorage
  useEffect(() => {
    localStorage.setItem('single_search_country', country);
    localStorage.setItem('single_search_city', city);
    localStorage.setItem('single_search_ageRange', ageRange);
    localStorage.setItem('single_search_gender', gender);
    localStorage.setItem('single_search_sexualOrientation', sexualOrientation);
  }, [country, city, ageRange, gender, sexualOrientation]);

  // Séparer les photos en deux groupes pour les carrousels
  const { leftTrackPhotos, rightTrackPhotos } = useMemo(() => {
    if (carouselPhotos.length === 0) {
      return { leftTrackPhotos: [], rightTrackPhotos: [] };
    }
    
    const middle = Math.ceil(carouselPhotos.length / 2);
    return {
      leftTrackPhotos: carouselPhotos.slice(0, middle),
      rightTrackPhotos: carouselPhotos.slice(middle)
    };
  }, [carouselPhotos]);

  const handleSearch = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    
    // Vérifier si l'utilisateur est authentifié
    if (!user) {
      // Sauvegarder le chemin actuel pour redirection après connexion
      localStorage.setItem('redirectAfterAuth', '/single-search');
      navigate('/login');
      return;
    }
    
    // Réinitialiser le compteur de crushs pour cette session de recherche
    setSessionCrushCount(0);
    
    // Réinitialiser l'état de déverrouillage à chaque nouvelle recherche
    setHasUnlockedShortTerm(false);
    setShowShortTermResults(false);
    
    // Récupérer les valeurs depuis les refs
    const countryValue = country;
    const cityValue = cityInputRef.current?.value || '';
    
    setHasSearched(true);
    await search({ 
      country: countryValue, 
      city: cityValue, 
      ageRange, 
      gender,
      sexualOrientation 
    });
  };

  const renderIntentionsBadges = (result: SingleSearchResult) => {
    if (result.relation_type === 'court' && result.short_term_intentions) {
      // Badges pour relation court terme
      return (
        <div className="flex flex-wrap gap-1 mt-2 max-w-full overflow-hidden">
          {result.short_term_intentions.includes('hookup') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              😳 {t('singleSearch.intentions.hookup')}
            </span>
          )}
          {result.short_term_intentions.includes('resto') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              🍽️ {t('singleSearch.intentions.resto')}
            </span>
          )}
          {result.short_term_intentions.includes('drink') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              🥂 {t('singleSearch.intentions.drink')}
            </span>
          )}
          {result.short_term_intentions.includes('seeWhat') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              {t('singleSearch.intentions.seeWhat')}
            </span>
          )}
          {result.short_term_intentions.includes('other') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              {t('singleSearch.intentions.other')}
            </span>
          )}
          {result.money_amount && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap flex items-center gap-1">
              💵 {result.money_amount} CFA
              {result.money_flow === 'give' 
                ? <ArrowUpCircle size={10} className="money-flow-icon-give ml-1" /> 
                : <ArrowDownCircle size={10} className="money-flow-icon-receive ml-1" />
              }
            </span>
          )}
        </div>
      );
    } else if (result.relation_type === 'long' && result.long_term_intentions) {
      // Badges pour relation long terme
      return (
        <div className="flex flex-wrap gap-1 mt-2 max-w-full overflow-hidden">
          {result.long_term_intentions.includes('marriage') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              💍 {t('singleSearch.intentions.marriage')}
            </span>
          )}
          {result.long_term_intentions.includes('children') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              👶 {t('singleSearch.intentions.children')}
            </span>
          )}
          {result.long_term_intentions.includes('fidelity') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              💘 {t('singleSearch.intentions.fidelity')}
            </span>
          )}
          {result.long_term_intentions.includes('polygamy') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              😍 {t('singleSearch.intentions.polygamy')}
            </span>
          )}
          {result.long_term_intentions.includes('free') && (
            <span className="text-[10px] bg-yellow-500/10 text-yellow-500 px-1.5 py-0.5 rounded-full whitespace-nowrap">
              🤗 {t('singleSearch.intentions.free')}
            </span>
          )}
        </div>
      );
    }
    return null;
  };

  const handleOpenPhotoGallery = (profile: SingleSearchResult, startIndex: number = 0) => {
    console.log("Ouverture de la galerie avec le profil:", profile);
    console.log("Description présente dans le profil:", profile.description);
    
    setSelectedProfile(profile);
    setCurrentPhotoIndex(startIndex);
    setShowPhotoGallery(true);
    setPhotoLoading(true);
    setImageLoadError(false);
  };

  const handleClosePhotoGallery = () => {
    setShowPhotoGallery(false);
    setSelectedProfile(null);
    setCurrentPhotoIndex(0);
  };

  const handleNextPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedProfile) return;
    
    setPhotoLoading(true);
    setImageLoadError(false);
    const photos = [selectedProfile.avatar_url, ...(selectedProfile.photos?.filter(Boolean) || [])];
    setCurrentPhotoIndex((prev) => (prev + 1) % photos.length);
  };

  const handlePrevPhoto = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!selectedProfile) return;
    
    setPhotoLoading(true);
    setImageLoadError(false);
    const photos = [selectedProfile.avatar_url, ...(selectedProfile.photos?.filter(Boolean) || [])];
    setCurrentPhotoIndex((prev) => (prev - 1 + photos.length) % photos.length);
  };

  // Fonction pour déverrouiller les résultats (temporaire, sans persistance)
  const unlockShortTermResults = () => {
    setHasUnlockedShortTerm(true);
    setShowShortTermResults(true);
    setShowPaymentModal(false);
  };

  // Fonction pour gérer le déverrouillage des résultats court terme
  const handleUnlockShortTermResults = () => {
    const shortTermPrice = getShortTermSearchUnlockPrice();
    console.log("Prix déverrouillage court terme:", shortTermPrice);
    
    // Si déjà déverrouillé dans cette session, on bascule l'affichage
    if (hasUnlockedShortTerm) {
      console.log("Déjà déverrouillé dans cette session, bascule d'affichage");
      setShowShortTermResults(!showShortTermResults);
      return;
    }
    
    // Si la fonctionnalité est désactivée OU le prix est à 0, déverrouiller directement
    if (!shortTermPrice.enabled || shortTermPrice.amount === 0) {
      console.log("Fonctionnalité désactivée ou prix à 0, déverrouillage gratuit");
      unlockShortTermResults();
      return;
    }
    
    // Sinon (fonctionnalité activée avec un prix > 0), on affiche le modal de paiement
    console.log("Ouverture du modal de paiement");
    setShowPaymentModal(true);
  };

  // Fonction pour gérer les clics sur le lien de contact
  const handleContactClick = async (e: React.MouseEvent, profileId: string, url: string) => {
    e.preventDefault();
    
    // Ouvrir le lien dans un nouvel onglet immédiatement
    window.open(url, '_blank');
    
    // Incrémenter le compteur de clics de manière asynchrone après
    await incrementContactClicks(profileId);
  };

  // Fonction séparée pour gérer les clics sur les photos du carrousel
  const handleCarouselPhotoClick = (photo: CarouselPhoto) => {
    window.open(photo.url, '_blank');
  };

  // Rendu du carrousel de photos
  const renderPhotoCarousel = () => {
    if (isLoadingPhotos) {
      return (
        <div className="bg-[#0A0A0A] rounded-2xl p-8 text-center my-4">
          <div className="flex flex-col items-center justify-center gap-3">
            <Loader className="w-8 h-8 text-primary-end animate-spin" />
            <p className="text-gray-400">{t('singleSearch.photoCarousel.loading')}</p>
          </div>
        </div>
      );
    }
    
    if (imageLoadError) {
      return (
        <div className="bg-[#0A0A0A] rounded-2xl p-8 text-center my-4">
          <p className="text-red-400">{t('singleSearch.photoCarousel.error')}</p>
        </div>
      );
    }
    
    if (carouselPhotos.length === 0) {
      return null; // Ne rien afficher si aucune photo n'est disponible
    }
    
    return (
      <div className="my-6">
        <h3 className={`text-lg font-medium ${gradientText} mb-3 text-center`}>
          {t('singleSearch.photoCarousel.title')}
        </h3>
        
        <div className="photo-carousel-container mx-auto overflow-hidden">
          {/* Piste gauche avec mouvement pendulaire */}
          <div className="flex photo-carousel-track-left">
            {leftTrackPhotos.map((photo, index) => (
              <div 
                key={`left-${photo.name}-${index}`}
                className={`photo-carousel-item flex-shrink-0 cursor-pointer mx-2 ${index % 5 === 0 ? 'mt-4' : index % 5 === 1 ? 'mt-6' : index % 5 === 2 ? 'mt-2' : index % 5 === 3 ? 'mt-8' : 'mt-5'}`}
                style={{ 
                  animationDelay: `${index * 0.5}s`, 
                  width: '120px', 
                  height: '180px' 
                }}
                onClick={() => handleCarouselPhotoClick(photo)}
              >
                <img 
                  src={photo.url} 
                  alt={`Carousel image ${index + 1}`}
                  className="object-cover w-full h-full rounded-lg shadow-lg"
                />
              </div>
            ))}
          </div>
          
          {/* Piste droite avec mouvement pendulaire inverse */}
          <div className="flex photo-carousel-track-right mt-10">
            {rightTrackPhotos.map((photo, index) => (
              <div 
                key={`right-${photo.name}-${index}`}
                className={`photo-carousel-item flex-shrink-0 cursor-pointer mx-2 ${index % 5 === 0 ? 'mt-3' : index % 5 === 1 ? 'mt-7' : index % 5 === 2 ? 'mt-1' : index % 5 === 3 ? 'mt-5' : 'mt-4'}`}
                style={{ 
                  animationDelay: `${index * 0.5}s`, 
                  width: '120px', 
                  height: '180px' 
                }}
                onClick={() => handleCarouselPhotoClick(photo)}
              >
                <img 
                  src={photo.url} 
                  alt={`Carousel image ${index + 1}`}
                  className="object-cover w-full h-full rounded-lg shadow-lg"
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  };

  const [likedProfiles, setLikedProfiles] = useState<Record<string, boolean>>({});
  const [crushCount, setCrushCount] = useState<number>(0);
  const [hiddenProfiles, setHiddenProfiles] = useState<Record<string, boolean>>({});
  const [showCrushLimitPopup, setShowCrushLimitPopup] = useState(false);
  
  // Charger les profils déjà likés lors du chargement de la page
  useEffect(() => {
    const loadLikedProfiles = async () => {
      if (!user) return;
      try {
        // Charger le nombre de crush
        const count = await countUserCrushes(user.id);
        setCrushCount(count);
        // Si des résultats sont déjà affichés, vérifier lesquels sont likés
        if (externalResults.length > 0) {
          const likes: Record<string, boolean> = {};
          await Promise.all(
            externalResults.map(async (result) => {
              // Déterminer si le profil est une annonce administrative
              const isAnnonceProfile = result && (
                (result.relation_type === 'court' && result.short_term_intentions?.some(i => 
                  ['hookup', 'resto', 'drink', 'seeWhat', 'other'].includes(i))) || 
                (result.relation_type === 'long' && result.long_term_intentions?.some(i => 
                  ['marriage', 'children', 'fidelity', 'polygamy', 'free'].includes(i)))
              );
              
              // Vérifier si le profil est liké, en tenant compte de son type
              const isLiked = await isProfileLiked(user.id, result.id, isAnnonceProfile);
              likes[result.id] = isLiked;
            })
          );
          setLikedProfiles(likes);
        }
      } catch (error) {
        console.error('Erreur lors du chargement des profils likés:', error);
      }
    };
    loadLikedProfiles();
    // Synchronisation du compteur de crushs si suppression ailleurs
    const handler = () => loadLikedProfiles();
    window.addEventListener('crushes-updated', handler);
    return () => window.removeEventListener('crushes-updated', handler);
  }, [user, externalResults]);

  // Réinitialiser le compteur de crushs à chaque nouvelle recherche
  useEffect(() => {
    if (hasSearched) {
      setCrushCount(0);
    }
  }, [hasSearched]);
  
  // Fonction pour gérer le clic sur le bouton cœur d'un profil
  const handleToggleCrush = async (e: React.MouseEvent, profileId: string) => {
    e.stopPropagation();

    if (!user) {
      // Sauvegarder le chemin actuel pour redirection après connexion
      localStorage.setItem('redirectAfterAuth', '/single-search');
      navigate('/login');
      return;
    }

    // Nouvelle règle : maximum 3 crushs par session de recherche
    if (!likedProfiles[profileId] && sessionCrushCount >= 3) {
      setShowCrushLimitPopup(true);
      return;
    }

    // Vérifier si les ID sont au format UUID valide
    const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
    if (!uuidRegex.test(user.id) || !uuidRegex.test(profileId)) {
      console.error('Format d\'ID non valide:', { userId: user.id, profileId });
      toast.error('Erreur technique: Format d\'identifiant invalide');
      return;
    }

    try {
      // Déterminer si ce profil provient de la table profil_annonce
      // Pour cela, on cherche le profil dans les résultats et on vérifie sa source
      const profileResult = externalResults.find(r => r.id === profileId);
      
      // Les profils d'annonces ont des caractéristiques spécifiques qu'on peut identifier
      // Par exemple, si c'est une annonce, le champ "full_name" correspond au champ "nom" de l'annonce
      const isAnnonceProfile = profileResult && (
        // Chercher des indices que c'est une annonce administrative
        (profileResult.relation_type === 'court' && profileResult.short_term_intentions?.some(i => 
          ['hookup', 'resto', 'drink', 'seeWhat', 'other'].includes(i))) || 
        (profileResult.relation_type === 'long' && profileResult.long_term_intentions?.some(i => 
          ['marriage', 'children', 'fidelity', 'polygamy', 'free'].includes(i)))
      );
      
      console.log('Profil identifié comme annonce:', isAnnonceProfile);
      
      const result = await toggleCrush(user.id, profileId, isAnnonceProfile);
      if (result.success) {
        setLikedProfiles(prev => ({
          ...prev,
          [profileId]: result.isLiked
        }));
        setCrushCount(prev => result.isLiked ? prev + 1 : prev - 1);
        
        // Mettre à jour également le compteur de session
        if (result.isLiked) {
          setSessionCrushCount(prev => prev + 1);
        } else {
          setSessionCrushCount(prev => Math.max(0, prev - 1));
        }
        
        toast.success(
          result.isLiked 
            ? 'Profil ajouté à vos crush!'
            : 'Profil retiré de vos crush!'
        );
      } else {
        toast.error(result.message || 'Une erreur est survenue');
      }
    } catch (error) {
      console.error('Erreur lors du toggle crush:', error);
      toast.error('Une erreur inattendue est survenue');
    }
  };
  
  // Fonction pour aller à la page My Crush
  const handleGoToCrushPage = (e: React.MouseEvent) => {
    e.stopPropagation();
    
    // Vérifier si l'utilisateur est connecté
    if (!user) {
      // Sauvegarder le chemin actuel pour redirection après connexion
      localStorage.setItem('redirectAfterAuth', '/single-search');
      navigate('/login');
      return;
    }
    
    navigate('/my-crush');
  };

  // Fonction pour masquer un profil
  const handleHideProfile = (e: React.MouseEvent, profileId: string) => {
    e.stopPropagation();
    setHiddenProfiles(prev => ({
      ...prev,
      [profileId]: true
    }));
  };

  // État pour les résultats de recherche
  const [sessionCrushCount, setSessionCrushCount] = useState(0); // Nombre de crushs ajoutés dans cette session de recherche

  return (
    <div className="min-h-screen bg-black p-4">
      {/* Photo Gallery Modal */}
      {showPhotoGallery && selectedProfile && (
        <div 
          className="fixed inset-0 bg-black bg-opacity-95 z-50 flex items-center justify-center p-4"
          onClick={handleClosePhotoGallery}
        >
          <button 
            className="absolute top-4 right-4 text-white z-10"
            onClick={handleClosePhotoGallery}
          >
            <X className="w-8 h-8" />
          </button>
          
          {/* Navigation buttons - Améliorés pour mobile */}
          <div className="absolute inset-x-0 top-1/2 -translate-y-1/2 flex justify-between px-3 z-10">
            <button 
              className="bg-black/80 rounded-full p-4 text-white shadow-lg active:bg-black/60"
              onClick={handlePrevPhoto}
              aria-label="Photo précédente"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
            
            <button 
              className="bg-black/80 rounded-full p-4 text-white shadow-lg active:bg-black/60"
              onClick={handleNextPhoto}
              aria-label="Photo suivante"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          </div>
          
          <div className="w-full h-full max-w-5xl max-h-[90vh] flex flex-col items-center justify-start px-4" 
               onClick={(e) => e.stopPropagation()}
               onTouchStart={onTouchStart}
               onTouchMove={onTouchMove}
               onTouchEnd={onTouchEnd}
          >
            {(() => {
              const photos = [selectedProfile.avatar_url, ...(selectedProfile.photos?.filter(Boolean) || [])];
              const currentPhoto = photos[currentPhotoIndex];
              
              return (
                <div className="w-full h-[80vh] flex flex-col items-center justify-start bg-black/20 backdrop-blur-sm rounded-3xl p-2 shadow-2xl relative"
                     onTouchStart={onTouchStart}
                     onTouchMove={onTouchMove}
                     onTouchEnd={onTouchEnd}
                >
                  {/* Indicateur de balayage */}
                  <div className="absolute top-2 left-0 right-0 flex justify-center z-10">
                    <div className="bg-black/40 px-3 py-1 rounded-full text-xs text-white/80 flex items-center">
                      <span className="mr-1">{t('singleSearch.gallery.swipeToNavigate')}</span>
                      <div className="flex gap-1">
                        <ChevronLeft className="w-3 h-3" />
                        <ChevronRight className="w-3 h-3" />
                      </div>
                    </div>
                  </div>
                  
                  {/* Conteneur pour les éléments du profil - titre et photo */}
                  <div className="flex flex-col items-center w-full mt-8 mb-2">
                    {/* Nom et âge en haut */}
                    <div className="font-medium text-lg bg-black/50 backdrop-blur-sm py-1 px-5 rounded-full mb-3">
                      {selectedProfile.full_name}, {selectedProfile.age} {t('singleSearch.results.age')}
                    </div>
                    
                    {/* Conteneur masque pour les images - réduit en hauteur */}
                    <div className="rounded-3xl overflow-hidden" style={{ 
                      width: '80vw', 
                      height: '45vh', 
                      maxWidth: '450px', 
                      maxHeight: '400px',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      backgroundColor: '#000'
                    }}>
                      <img 
                        src={currentPhoto} 
                        alt={`Photo ${currentPhotoIndex + 1} de ${selectedProfile.full_name}`}
                        style={{ 
                          width: '100%',
                          height: '100%',
                          objectFit: 'contain'
                        }}
                      />
                    </div>
                    
                    {/* Description - espace agrandi pour accueillir plus de texte */}
                    <div className="w-full max-w-md mt-3" style={{ maxHeight: '30vh', overflow: 'auto' }}>
                      {(() => { console.log("Description de l'utilisateur:", selectedProfile.description); return null; })()}
                      {selectedProfile.description && (
                        <div className="bg-black/40 backdrop-blur-sm rounded-xl p-4 text-white">
                          <p className="text-sm text-white">{selectedProfile.description}</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              );
            })()}
          </div>
        </div>
      )}

      <div className="max-w-2xl mx-auto">
        <div className="text-center mb-3">
          <h2 className={`text-3xl lg:text-4xl xl:text-5xl font-bold ${gradientText} mb-0`}>
            {t('singleSearch.title')}
          </h2>
          <p className="text-gray-400 text-xs lg:text-sm italic font-light tracking-wide">
            {t('singleSearch.subtitle')}
          </p>
        </div>
        
        <form onSubmit={handleSearch} className="space-y-2 bg-[#0A0A0A] p-4 rounded-2xl">
          <div className="space-y-3">
            {/* Pays et Ville sur la même ligne */}
            <div className="grid grid-cols-2 gap-3">
              <div className="relative">
                <MapPin className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  value={country}
                  onChange={(e) => setCountry(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-2 focus:outline-none focus:border-primary-end text-white"
                  placeholder={t('singleSearch.form.country')}
                />
              </div>
              <div className="relative">
                <Building className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-500" />
                <input
                  type="text"
                  ref={cityInputRef}
                  defaultValue={city}
                  onChange={(e) => setCity(e.target.value)}
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl pl-12 pr-4 py-2 focus:outline-none focus:border-primary-end text-white"
                  placeholder={t('singleSearch.form.city')}
                />
              </div>
            </div>

            {/* Tranche d'âge et sexe sur la même ligne */}
            <div className="grid grid-cols-2 gap-3">
              {/* Tranche d'âge */}
              <div>
                <label className="block text-gray-400 mb-1 text-sm">{t('singleSearch.form.ageRange')}</label>
                <div className="relative" ref={ageRangeRef}>
                  <div 
                    className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-2 text-white cursor-pointer flex justify-between items-center"
                    onClick={() => setShowAgeRangeOptions(!showAgeRangeOptions)}
                  >
                    <span>{t(`singleSearch.form.ageRanges.${ageRange}`)}</span>
                    <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                    </svg>
                  </div>
                  {showAgeRangeOptions && (
                    <div className="absolute z-10 mt-1 w-full bg-[#121212] border border-gray-800 rounded-xl shadow-lg overflow-hidden">
                      {['18-25', '26-35', '36-45', '46-55', '56+'].map((range) => (
                        <div
                          key={range}
                          className={`px-4 py-2 cursor-pointer hover:bg-[#1A1A1A] ${ageRange === range ? 'bg-primary-end/10 text-primary-end' : 'text-white'}`}
                          onClick={() => {
                            setAgeRange(range);
                            setShowAgeRangeOptions(false);
                          }}
                        >
                          {t(`singleSearch.form.ageRanges.${range}`)}
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {/* Sexe */}
              <div>
                <label className="block text-gray-400 mb-1 text-sm">{t('singleSearch.form.gender')}</label>
                <div className="relative">
                  <select
                    value={gender}
                    onChange={e => setGender(e.target.value)}
                    className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-2 text-white focus:outline-none focus:border-primary-end appearance-none"
                  >
                    <option value="homme">{t('singleSearch.form.genderOptions.homme')}</option>
                    <option value="femme">{t('singleSearch.form.genderOptions.femme')}</option>
                  </select>
                  <svg className="w-5 h-5 text-gray-500 absolute right-4 top-1/2 -translate-y-1/2 pointer-events-none" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
              </div>
            </div>

            {/* Orientation sexuelle en mode liste */}
            <div>
              <label className="block text-gray-400 mb-1 text-sm">{t('profile.datingProfile.orientation.title')}</label>
              <div className="relative" ref={orientationRef}>
                <div
                  className="w-full bg-[#0A0A0A] border border-gray-800 rounded-xl px-4 py-2 text-white cursor-pointer flex justify-between items-center"
                  onClick={() => setShowOrientationOptions(!showOrientationOptions)}
                >
                  <span>
                    {sexualOrientation ? t(`profile.datingProfile.orientation.${sexualOrientation}`) : t(`profile.datingProfile.orientation.hetero`)}
                  </span>
                  <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M19 9l-7 7-7-7"></path>
                  </svg>
                </div>
                
                {showOrientationOptions && (
                  <div className="absolute z-10 mt-1 w-full bg-[#121212] border border-gray-800 rounded-xl shadow-lg overflow-hidden">
                    {['hetero', 'gay', 'lesbian', 'bi'].map((orientation) => (
                      <div
                        key={orientation}
                        className={`px-4 py-2 cursor-pointer hover:bg-[#1A1A1A] ${sexualOrientation === orientation ? 'bg-primary-end/10 text-primary-end' : 'text-white'}`}
                        onClick={() => {
                          setSexualOrientation(orientation);
                          setShowOrientationOptions(false);
                        }}
                      >
                        {t(`profile.datingProfile.orientation.${orientation}`)}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          </div>

          <button
            type="submit"
            disabled={!isSearchFormValid() || externalIsSearching}
            className={`w-full ${gradientButton} text-white font-medium py-3 px-6 rounded-xl transition-all flex items-center justify-center gap-2 text-lg mt-4 ${!isSearchFormValid() && !externalIsSearching ? 'opacity-50 cursor-not-allowed' : ''}`}
          >
            {externalIsSearching ? (
              <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
            ) : (
              <Search className="w-5 h-5" />
            )}
            {externalIsSearching ? t('singleSearch.form.searching') : t('singleSearch.form.search')}
          </button>
        </form>

        {/* Crawl informatif déplacé ici */}
        {false && (
        <div className="w-full overflow-hidden bg-gradient-to-r from-black to-[#0A0A0A] rounded-xl py-3 border-l border-primary-start/30 shadow-md relative crawl-container crawl-glow light-sweep mt-1">
          {/* Effet de bordure brillante - opacité réduite */}
          <div className="absolute inset-0 border border-primary-start/10 rounded-xl"></div>
          <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-start to-primary-end opacity-30"></div>
          <div className="absolute top-0 left-0 bottom-0 w-0.5 bg-gradient-to-b from-primary-start to-primary-end opacity-40"></div>
          
          {/* Effet lumineux accentué sur la bordure gauche - subtil */}
          <div className="absolute left-0 top-0 bottom-0 w-1 bg-gradient-to-b from-primary-start via-primary-end to-primary-start opacity-20 blur-sm"></div>
          <div className="absolute left-0 top-1/2 -translate-y-1/2 h-12 w-0.5 bg-primary-end opacity-40 blur-[1px] animate-pulse"></div>
          
          <div className="relative pl-12">
            <div className="crawl-text text-sm">
              <span>
                <span className="highlight-word">{t('singleSearch.crawlText.part1')}</span>
                {t('singleSearch.crawlText.part3')}
                <span className="highlight-word">{t('singleSearch.crawlText.part4')}</span>
                {t('singleSearch.crawlText.part6')}
                <span className="mx-28">•</span>
                <span className="highlight-word">{t('singleSearch.crawlText.part1')}</span>
                {t('singleSearch.crawlText.part3')}
                <span className="highlight-word">{t('singleSearch.crawlText.part4')}</span>
                {t('singleSearch.crawlText.part6')}
                <span className="mx-28">•</span>
                <span className="highlight-word">{t('singleSearch.crawlText.part1')}</span>
                {t('singleSearch.crawlText.part3')}
                <span className="highlight-word">{t('singleSearch.crawlText.part4')}</span>
                {t('singleSearch.crawlText.part6')}
              </span>
            </div>
          </div>
        </div>
        )}

        {/* Afficher le carrousel uniquement quand aucune recherche n'est en cours */}
        {!hasSearched && carouselPhotos.length > 0 && renderPhotoCarousel()}

        {/* Résultats de recherche */}
        {hasSearched && (
          <div className="mt-8">
            <h3 className={`text-xl font-bold ${gradientText} mb-4 flex items-center justify-between`}>
              <div>
                {externalIsSearching 
                  ? t('singleSearch.results.searching')
                  : externalResults.length > 0 
                    ? t('singleSearch.results.found', { 
                        count: externalResults.length, 
                        plural: externalResults.length > 1 ? 's' : '' 
                      })
                    : t('singleSearch.results.notFound')}
              </div>
              {!externalIsSearching && externalResults.length > 0 && (
                <button 
                  className="w-8 h-8 bg-[#1A1A1A] rounded-full flex items-center justify-center relative mr-2"
                  onClick={handleGoToCrushPage}
                >
                  <Heart className={`w-5 h-5 ${crushCount > 0 ? 'text-primary-end fill-primary-end' : 'text-white'} hover:text-primary-end transition-colors`} />
                  {crushCount > 0 && (
                    <span className="absolute -top-1 -right-1 text-white text-xs font-bold">
                      {crushCount > 9 ? '9+' : crushCount}
                    </span>
                  )}
                </button>
              )}
            </h3>
            
            {externalIsSearching ? (
              <div className="bg-[#0A0A0A] rounded-xl p-8 flex items-center justify-center">
                <div className="w-8 h-8 border-4 border-primary-end border-t-transparent rounded-full animate-spin mr-3"></div>
                <p className="text-gray-400">{t('singleSearch.results.searchingMessage')}</p>
              </div>
            ) : externalResults.length === 0 ? (
              <div className="bg-[#0A0A0A] rounded-xl p-8 text-center">
                <p className="text-gray-500 text-sm">{t('singleSearch.results.tryModify')}</p>
              </div>
            ) : (
              <div className="space-y-6">
                {/* Grouper et afficher les résultats par type de relation */}
                {(() => {
                  // Séparer les résultats par type
                  const longTermResultsList = externalResults.filter((r: SingleSearchResult) => r.relation_type === 'long' && !hiddenProfiles[r.id]);
                  const shortTermResultsList = externalResults.filter((r: SingleSearchResult) => r.relation_type === 'court' && !hiddenProfiles[r.id]);
                  
                  return (
                    <>
                      {/* Section Court terme (si présente) - MAINTENANT EN PREMIER */}
                      {shortTermResultsList.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-3 rounded-xl bg-gradient-to-r from-black to-[#0A0A0A] border-l-4 border-primary-start py-3 px-4 shadow-md relative overflow-hidden">
                            {/* Ajout d'un effet de brillance en arrière-plan */}
                            <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-primary-start/10 to-primary-end/30 rounded-xl"></div>
                            {/* Ajout d'une ligne décorative en bas */}
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-start to-primary-end opacity-70"></div>
                            
                            <div className="flex flex-col relative z-10">
                              <h4 className={`font-semibold ${gradientText}`}>
                                {t('singleSearch.results.shortTerm')} 
                              </h4>
                              <p className="text-sm text-gray-400 mt-1">
                                {shortTermResultsList.length} {t('singleSearch.results.foundSimple', { plural: shortTermResultsList.length > 1 ? 's' : '' })}
                              </p>
                            </div>
                            <div className="flex items-center relative z-10">
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "0s"}}>🍆</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "0.5s"}}>🍑</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "1s"}}>🥂</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "2s"}}>💵</span>
                            </div>
                            <button 
                              onClick={handleUnlockShortTermResults}
                              className={`${
                                !hasUnlockedShortTerm && getShortTermSearchUnlockPrice().enabled && getShortTermSearchUnlockPrice().amount > 0 
                                  ? 'bg-primary-end/20 px-2 py-1 rounded-lg flex items-center gap-1' 
                                  : 'text-gray-400 hover:text-white transition-colors flex items-center'
                              } relative z-10`}
                            >
                              {!hasUnlockedShortTerm && getShortTermSearchUnlockPrice().enabled ? (
                                <Lock className="w-5 h-5 text-yellow-500 lock-animation" />
                              ) : (
                                showShortTermResults ? 
                                <Eye className="w-5 h-5" /> : 
                                <EyeOff className="w-5 h-5" />
                              )}
                            </button>
                          </div>
                          {(showShortTermResults && hasUnlockedShortTerm) && (
                            <div className="space-y-4">
                              {shortTermResultsList.map((result: SingleSearchResult) => (
                                <div key={result.id} className="bg-[#0A0A0A] rounded-xl p-4 flex gap-4 w-full max-w-full overflow-hidden">
                                  {/* Photo de profil avec âge superposé */}
                                  <div 
                                    className="w-20 h-20 min-w-[5rem] rounded-xl overflow-hidden flex-shrink-0 relative cursor-pointer"
                                    onClick={() => handleOpenPhotoGallery(result, 0)}
                                  >
                                    {/* Bouton X pour masquer */}
                                    <button 
                                      className="absolute top-1 left-1 w-5 h-5 bg-black/60 flex items-center justify-center rounded-full z-10 profile-hide-button"
                                      onClick={(e) => handleHideProfile(e, result.id)}
                                      title={t('singleSearch.hide')}
                                    >
                                      <X className="w-3 h-3 text-white" />
                                    </button>
                                    
                                    {result.avatar_url ? (
                                      <img 
                                        src={result.avatar_url} 
                                        alt={result.full_name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-[#121212]">
                                        <User className="w-8 h-8 text-gray-600" />
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 right-0 bg-black/60 text-white text-xs font-medium py-1 px-2 rounded-tl-md">
                                      {result.age} {t('singleSearch.results.age')}
                                    </div>
                                    
                                    {/* Bouton coeur */}
                                    <button 
                                      className="absolute bottom-0 left-0 w-7 h-7 bg-black/60 flex items-center justify-center rounded-tr-md"
                                      onClick={(e) => handleToggleCrush(e, result.id)}
                                    >
                                      <Heart className={`w-4 h-4 ${likedProfiles[result.id] ? 'text-primary-end fill-primary-end' : 'text-white'} hover:text-primary-end transition-colors`} />
                                    </button>
                                  </div>
                                  
                                  {/* Informations */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col w-full">
                                      <div className="flex items-center w-full justify-between">
                                        <h4 className="text-white font-medium text-lg truncate pr-2 flex-1">
                                          {result.full_name}
                                        </h4>
                                        {result.custom_link_url && (
                                          <a 
                                            href={result.custom_link_url}
                                            onClick={(e) => handleContactClick(e, result.id, result.custom_link_url as string)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-primary-start to-primary-end hover:opacity-90 transition-opacity"
                                          >
                                            <Phone className="w-3 h-3 text-white" />
                                          </a>
                                        )}
                                      </div>
                                      
                                      {/* Badges d'intentions */}
                                      {renderIntentionsBadges(result)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Section Long terme (si présente) - MAINTENANT EN SECOND */}
                      {longTermResultsList.length > 0 && (
                        <div>
                          <div className="flex items-center justify-between mb-3 rounded-xl bg-gradient-to-r from-black to-[#0A0A0A] border-l-4 border-primary-end py-3 px-4 shadow-md relative overflow-hidden">
                            {/* Ajout d'un effet de brillance en arrière-plan */}
                            <div className="absolute inset-0 opacity-20 bg-gradient-to-r from-primary-start/10 to-primary-end/30 rounded-xl"></div>
                            {/* Ajout d'une ligne décorative en bas */}
                            <div className="absolute bottom-0 left-0 right-0 h-0.5 bg-gradient-to-r from-primary-start to-primary-end opacity-70"></div>
                            
                            <div className="flex flex-col relative z-10">
                              <h4 className={`font-semibold ${gradientText}`}>
                                {t('singleSearch.results.longTerm')}
                              </h4>
                              <p className="text-sm text-gray-400 mt-1">
                                {longTermResultsList.length} {t('singleSearch.results.foundSimple', { plural: longTermResultsList.length > 1 ? 's' : '' })}
                              </p>
                            </div>
                            <div className="flex items-center relative z-10">
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "0s"}}>💘</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "0.5s"}}>💍</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "1s"}}>😍</span>
                              <span className="ml-2 text-lg animate-float" style={{animationDelay: "1.5s"}}>👶</span>
                            </div>
                            <button 
                              onClick={() => setShowLongTermResults(!showLongTermResults)}
                              className="text-gray-400 hover:text-white transition-colors relative z-10"
                            >
                              {showLongTermResults ? 
                                <Eye className="w-5 h-5" /> : 
                                <EyeOff className="w-5 h-5" />
                              }
                            </button>
                          </div>
                          {showLongTermResults && (
                            <div className="space-y-4">
                              {longTermResultsList.map((result: SingleSearchResult) => (
                                <div key={result.id} className="bg-[#0A0A0A] rounded-xl p-4 flex gap-4 w-full max-w-full overflow-hidden">
                                  {/* Photo de profil avec âge superposé */}
                                  <div 
                                    className="w-20 h-20 min-w-[5rem] rounded-xl overflow-hidden flex-shrink-0 relative cursor-pointer"
                                    onClick={() => handleOpenPhotoGallery(result, 0)}
                                  >
                                    {/* Bouton X pour masquer */}
                                    <button 
                                      className="absolute top-1 left-1 w-5 h-5 bg-black/60 flex items-center justify-center rounded-full z-10 profile-hide-button"
                                      onClick={(e) => handleHideProfile(e, result.id)}
                                      title={t('singleSearch.hide')}
                                    >
                                      <X className="w-3 h-3 text-white" />
                                    </button>
                                    
                                    {result.avatar_url ? (
                                      <img 
                                        src={result.avatar_url} 
                                        alt={result.full_name}
                                        className="w-full h-full object-cover"
                                      />
                                    ) : (
                                      <div className="w-full h-full flex items-center justify-center bg-[#121212]">
                                        <User className="w-8 h-8 text-gray-600" />
                                      </div>
                                    )}
                                    <div className="absolute bottom-0 right-0 bg-black/60 text-white text-xs font-medium py-1 px-2 rounded-tl-md">
                                      {result.age} {t('singleSearch.results.age')}
                                    </div>
                                    
                                    {/* Bouton coeur */}
                                    <button 
                                      className="absolute bottom-0 left-0 w-7 h-7 bg-black/60 flex items-center justify-center rounded-tr-md"
                                      onClick={(e) => handleToggleCrush(e, result.id)}
                                    >
                                      <Heart className={`w-4 h-4 ${likedProfiles[result.id] ? 'text-primary-end fill-primary-end' : 'text-white'} hover:text-primary-end transition-colors`} />
                                    </button>
                                  </div>
                                  
                                  {/* Informations */}
                                  <div className="flex-1 min-w-0">
                                    <div className="flex flex-col w-full">
                                      <div className="flex items-center w-full justify-between">
                                        <h4 className="text-white font-medium text-lg truncate pr-2 flex-1">
                                          {result.full_name}
                                        </h4>
                                        {result.custom_link_url && (
                                          <a 
                                            href={result.custom_link_url}
                                            onClick={(e) => handleContactClick(e, result.id, result.custom_link_url as string)}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="flex-shrink-0 inline-flex items-center justify-center w-6 h-6 rounded-full bg-gradient-to-r from-primary-start to-primary-end hover:opacity-90 transition-opacity"
                                          >
                                            <Phone className="w-3 h-3 text-white" />
                                          </a>
                                        )}
                                      </div>
                                      
                                      {/* Badges d'intentions */}
                                      {renderIntentionsBadges(result)}
                                    </div>
                                  </div>
                                </div>
                              ))}
                            </div>
                          )}
                        </div>
                      )}
                    </>
                  );
                })()}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal de paiement */}
      {showPaymentModal && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <PaymentFlow 
            amount={getShortTermSearchUnlockPrice().amount}
            currency={getShortTermSearchUnlockPrice().currency}
            onSuccess={() => {
              unlockShortTermResults();
              setShowPaymentModal(false);
            }}
            onCancel={() => {
              setShowPaymentModal(false);
            }}
            service="SHORT_TERM_RESULTS_UNLOCK"
            userName={user?.user_metadata?.full_name || user?.email || 'Utilisateur'}
          />
        </div>
      )}

      {/* Pop-up de limite de crushes */}
      {showCrushLimitPopup && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4">
          <div className="animate-fadeIn relative">
            <div className="bg-gradient-to-r from-primary-start to-primary-end p-px rounded-xl shadow-lg max-w-md w-full">
              <div className="bg-[#0A0A0A] rounded-xl p-4 relative">
                <button 
                  onClick={() => setShowCrushLimitPopup(false)}
                  className="absolute top-2 right-2 text-gray-400 hover:text-white"
                >
                  <X className="w-4 h-4" />
                </button>
                
                <h3 className={`text-lg font-semibold ${gradientText} mb-2`}>
                  {t('singleSearch.crushLimitPopup.title')}
                </h3>
                
                <p className="text-white text-sm mb-3">
                  {t('singleSearch.crushLimitPopup.message')}
                </p>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default SingleSearch; 